/**
 * Extracts plain text from an HTML-escaped string by unescaping HTML entities
 * and removing all HTML tags. Node.js compatible version.
 *
 * @param htmlString - The HTML-escaped string to process
 * @returns The extracted plain text without HTML tags
 */
export function extractTextFromHtml(htmlString: string): string {
  if (!htmlString) {
    return '';
  }

  // Step 1: Unescape HTML entities
  const unescapedHtml = unescapeHtml(htmlString);

  // Step 2: Add spaces where needed before removing tags
  const htmlWithSpaces = unescapedHtml
    // Add a space before paragraph and list item closings to preserve text separation
    .replace(/<\/p>\s*<p/g, '</p> <p')
    .replace(/<\/li>\s*<li/g, '</li> <li')
    // Add spaces between block elements that might cause text to run together
    .replace(/<\/[^>]+>\s*<[^>]+>/g, match => {
      // If there's already a space, don't add another one
      if (/>\s+</.test(match)) {
        return match;
      }
      return match.replace(/></, '> <');
    });

  // Step 3: Remove HTML tags with regex
  return htmlWithSpaces
    .replace(/<[^>]*>/g, '') // Remove HTML tags
    .replace(/\s+/g, ' ') // Normalize whitespace
    .trim(); // Trim leading/trailing whitespace
}

/**
 * Unescapes HTML entities in a string
 *
 * @param html - The HTML-escaped string
 * @returns Unescaped HTML string
 */
function unescapeHtml(html: string): string {
  return html
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ');
}
